/**
 * Page Data Cache Service
 *
 * Caches page data from all portal pages for AI Assistant context.
 * Stores data in memory with TTL and localStorage for persistence.
 */

import { useCallback, useMemo } from 'react';

const CACHE_KEY = 'sylink_page_data_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 50; // Maximum pages to cache

class PageDataCacheService {
  constructor() {
    this.cache = new Map();
    this.loadFromStorage();
  }

  /**
   * Safely get className as string (handles SVG elements)
   */
  getClassNameString(element) {
    if (!element) return '';
    const className = element.className;
    if (typeof className === 'string') return className;
    // SVGAnimatedString has baseVal property
    if (className && typeof className.baseVal === 'string') return className.baseVal;
    return '';
  }

  /**
   * Load cache from localStorage
   */
  loadFromStorage() {
    try {
      const stored = localStorage.getItem(CACHE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        Object.entries(parsed).forEach(([key, value]) => {
          // Only load if not expired
          if (value.timestamp && Date.now() - value.timestamp < CACHE_TTL) {
            this.cache.set(key, value);
          }
        });
      }
    } catch (err) {
      console.warn('Failed to load page cache from storage:', err);
    }
  }

  /**
   * Save cache to localStorage
   */
  saveToStorage() {
    try {
      const obj = {};
      this.cache.forEach((value, key) => {
        obj[key] = value;
      });
      localStorage.setItem(CACHE_KEY, JSON.stringify(obj));
    } catch (err) {
      console.warn('Failed to save page cache to storage:', err);
    }
  }

  /**
   * Generate cache key from path and params
   */
  getCacheKey(path, params = {}) {
    const paramStr = Object.entries(params)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join('&');
    return paramStr ? `${path}?${paramStr}` : path;
  }

  /**
   * Set page data in cache
   */
  set(path, data, params = {}) {
    const key = this.getCacheKey(path, params);

    // Evict oldest entries if cache is full
    if (this.cache.size >= MAX_CACHE_SIZE) {
      const entries = Array.from(this.cache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = entries.slice(0, 10).map(([k]) => k);
      toRemove.forEach(k => this.cache.delete(k));
    }

    this.cache.set(key, {
      path,
      params,
      data,
      timestamp: Date.now()
    });

    this.saveToStorage();
  }

  /**
   * Get page data from cache
   */
  get(path, params = {}) {
    const key = this.getCacheKey(path, params);
    const cached = this.cache.get(key);

    if (!cached) return null;

    // Check if expired
    if (Date.now() - cached.timestamp > CACHE_TTL) {
      this.cache.delete(key);
      this.saveToStorage();
      return null;
    }

    return cached.data;
  }

  /**
   * Check if cache has valid entry
   */
  has(path, params = {}) {
    return this.get(path, params) !== null;
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
    localStorage.removeItem(CACHE_KEY);
  }

  /**
   * Get all cached pages for AI context
   */
  getAllCachedData() {
    const result = [];
    this.cache.forEach((value, key) => {
      if (Date.now() - value.timestamp < CACHE_TTL) {
        result.push({
          key,
          path: value.path,
          params: value.params,
          data: value.data,
          timestamp: value.timestamp
        });
      }
    });
    return result;
  }

  /**
   * Get summary of cached pages for AI context
   */
  getCacheSummary() {
    const pages = [];
    this.cache.forEach((value, key) => {
      if (Date.now() - value.timestamp < CACHE_TTL) {
        pages.push({
          path: value.path,
          hasData: !!value.data,
          dataKeys: value.data ? Object.keys(value.data) : [],
          age: Math.round((Date.now() - value.timestamp) / 1000) + 's'
        });
      }
    });
    return {
      totalPages: pages.length,
      pages
    };
  }

  /**
   * Extract page data from DOM
   */
  extractPageData(path) {
    const data = {
      path,
      timestamp: new Date().toISOString(),
      title: document.title,
      metadata: {}
    };

    // Extract tables
    const tables = document.querySelectorAll('table');
    if (tables.length > 0) {
      data.tables = [];
      tables.forEach((table, idx) => {
        const headers = [];
        const rows = [];

        table.querySelectorAll('th').forEach(th => {
          headers.push(th.innerText?.trim());
        });

        table.querySelectorAll('tbody tr').forEach((row, rowIdx) => {
          if (rowIdx < 20) { // Limit to 20 rows
            const cells = [];
            row.querySelectorAll('td').forEach(td => {
              cells.push(td.innerText?.trim()?.substring(0, 200));
            });
            rows.push(cells);
          }
        });

        if (headers.length > 0 || rows.length > 0) {
          data.tables.push({
            index: idx,
            headers,
            rows,
            totalRows: table.querySelectorAll('tbody tr').length
          });
        }
      });
    }

    // Extract cards with vulnerability info
    const vulnCards = document.querySelectorAll('[class*="vulnerability"], [class*="vuln"]');
    if (vulnCards.length > 0) {
      data.vulnerabilities = [];
      vulnCards.forEach((card, idx) => {
        if (idx < 50) { // Limit
          const cveMatch = card.innerText?.match(/CVE-\d{4}-\d+/g);
          data.vulnerabilities.push({
            text: card.innerText?.substring(0, 500),
            cves: cveMatch || [],
            severity: card.querySelector('[class*="severity"]')?.innerText ||
                      this.getClassNameString(card.querySelector('[class*="critical"], [class*="high"], [class*="medium"], [class*="low"]')).match(/(critical|high|medium|low)/i)?.[0]
          });
        }
      });
    }

    // Extract scan info
    const scanElements = document.querySelectorAll('[class*="scan"]');
    if (scanElements.length > 0) {
      data.scans = [];
      scanElements.forEach((el, idx) => {
        if (idx < 20) {
          data.scans.push({
            status: el.querySelector('[class*="status"]')?.innerText,
            target: el.querySelector('[class*="target"]')?.innerText,
            progress: el.querySelector('[class*="progress"]')?.innerText,
            text: el.innerText?.substring(0, 300)
          });
        }
      });
    }

    // Extract stats/metrics cards
    const statsCards = document.querySelectorAll('[class*="stat"], [class*="metric"], [class*="card"]');
    if (statsCards.length > 0) {
      data.stats = [];
      statsCards.forEach((card, idx) => {
        if (idx < 30) {
          const title = card.querySelector('h1, h2, h3, h4, h5, h6, [class*="title"]')?.innerText;
          const value = card.querySelector('[class*="value"], [class*="number"]')?.innerText;
          if (title || value) {
            data.stats.push({
              title: title?.substring(0, 100),
              value: value?.substring(0, 100)
            });
          }
        }
      });
    }

    // Extract forms
    const forms = document.querySelectorAll('form');
    if (forms.length > 0) {
      data.forms = [];
      forms.forEach((form, idx) => {
        const fields = [];
        form.querySelectorAll('input, select, textarea').forEach(input => {
          fields.push({
            name: input.name || input.id,
            type: input.type,
            placeholder: input.placeholder,
            hasValue: !!input.value && input.type !== 'password'
          });
        });
        if (fields.length > 0) {
          data.forms.push({ index: idx, fields });
        }
      });
    }

    // Extract main content text
    const mainContent = document.querySelector('main, [role="main"], .main-content, #main');
    if (mainContent) {
      data.mainContent = mainContent.innerText?.substring(0, 2000);
    }

    // Extract headings for structure
    const headings = document.querySelectorAll('h1, h2, h3');
    if (headings.length > 0) {
      data.headings = [];
      headings.forEach(h => {
        const text = h.innerText?.trim();
        if (text) {
          data.headings.push({
            level: h.tagName,
            text: text.substring(0, 200)
          });
        }
      });
    }

    // Extract alerts/notifications
    const alerts = document.querySelectorAll('[class*="alert"], [class*="notification"], [class*="toast"]');
    if (alerts.length > 0) {
      data.alerts = [];
      alerts.forEach(alert => {
        data.alerts.push({
          text: alert.innerText?.substring(0, 200),
          type: this.getClassNameString(alert).match(/(success|error|warning|info)/i)?.[0]
        });
      });
    }

    return data;
  }

  /**
   * Auto-cache current page
   */
  cacheCurrentPage() {
    const path = window.location.pathname;
    const params = Object.fromEntries(new URLSearchParams(window.location.search));
    const data = this.extractPageData(path);
    this.set(path, data, params);
    return data;
  }
}

// Singleton instance
const pageDataCache = new PageDataCacheService();

export default pageDataCache;

// Hook for React components - memoized to prevent infinite re-renders
export const usePageDataCache = () => {
  // Memoize all functions to prevent new references on each render
  const cacheCurrentPage = useCallback(() => pageDataCache.cacheCurrentPage(), []);
  const getPageData = useCallback((path, params) => pageDataCache.get(path, params), []);
  const setPageData = useCallback((path, data, params) => pageDataCache.set(path, data, params), []);
  const getAllCachedData = useCallback(() => pageDataCache.getAllCachedData(), []);
  const getCacheSummary = useCallback(() => pageDataCache.getCacheSummary(), []);
  const clearCache = useCallback(() => pageDataCache.clear(), []);
  const extractPageData = useCallback((path) => pageDataCache.extractPageData(path), []);

  return useMemo(() => ({
    cache: pageDataCache,
    cacheCurrentPage,
    getPageData,
    setPageData,
    getAllCachedData,
    getCacheSummary,
    clearCache,
    extractPageData
  }), [cacheCurrentPage, getPageData, setPageData, getAllCachedData, getCacheSummary, clearCache, extractPageData]);
};
